import React, { useState, useEffect } from 'react';
import userService, { User, CreateUserRequest, UpdateUserRequest, PaginatedResponse } from '../services/userService';
import './UserManagement.css';

interface UserManagementProps {
  onClose?: () => void;
}

const UserManagement: React.FC<UserManagementProps> = ({ onClose }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedAirport, setSelectedAirport] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [userStats, setUserStats] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('ASC');
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);

  // Формы
  const [createForm, setCreateForm] = useState<CreateUserRequest>({
    email: '',
    username: '',
    password: '',
    name: '',
    surname: '',
    patronymic: '',
    role: 'USER',
    position: 'DRIVER',
    airport: '',
    enabled: true
  });

  const [editForm, setEditForm] = useState<UpdateUserRequest>({
    email: '',
    username: '',
    password: '',
    name: '',
    surname: '',
    patronymic: '',
    role: 'USER',
    position: 'DRIVER',
    airport: '',
    enabled: true
  });

  const roles = ['USER', 'MODERATOR', 'ADMIN'];
  const airports = ['SVO', 'DME', 'VKO', 'ASTANA', 'ALMATY'];
  const positions = ['DRIVER', 'MANAGER', 'OPERATOR'];
  const sortOptions = [
    { value: 'id', label: 'ID' },
    { value: 'name', label: 'Имя' },
    { value: 'surname', label: 'Фамилия' },
    { value: 'email', label: 'Email' },
    { value: 'username', label: 'Логин' },
    { value: 'role', label: 'Роль' },
    { value: 'position', label: 'Должность' },
    { value: 'airport', label: 'Аэропорт' },
    { value: 'createdAt', label: 'Дата создания' }
  ];

  useEffect(() => {
    fetchUsers();
    fetchUserStats();
  }, [currentPage, pageSize, sortBy, sortDir]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: PaginatedResponse<User> = await userService.getUsers(currentPage, pageSize, sortBy, sortDir);
      setUsers(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Ошибка при загрузке пользователей');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const stats = await userService.getUserStats();
      setUserStats(stats);
    } catch (err) {
      console.error('Error fetching user stats:', err);
    }
  };

  const handleCreateUser = async () => {
    try {
      if (!createForm.email || !createForm.username || !createForm.password || !createForm.name || !createForm.surname || !createForm.position) {
        alert('Пожалуйста, заполните все обязательные поля');
        return;
      }

      await userService.createUser(createForm);
      setIsCreateModalOpen(false);
      setCreateForm({
        email: '',
        username: '',
        password: '',
        name: '',
        surname: '',
        patronymic: '',
        role: 'USER',
        position: 'DRIVER',
        airport: '',
        enabled: true
      });
      fetchUsers();
      alert('Пользователь успешно создан!');
    } catch (err) {
      console.error('Error creating user:', err);
      alert('Ошибка при создании пользователя');
    }
  };

  const handleUpdateUser = async () => {
    try {
      if (!editingUser) return;

      const updateData: UpdateUserRequest = {
        position: editForm.position || 'DRIVER' // position теперь обязательное поле
      };
      if (editForm.email) updateData.email = editForm.email;
      if (editForm.username) updateData.username = editForm.username;
      if (editForm.password) updateData.password = editForm.password;
      if (editForm.name) updateData.name = editForm.name;
      if (editForm.surname) updateData.surname = editForm.surname;
      if (editForm.patronymic !== undefined) updateData.patronymic = editForm.patronymic;
      if (editForm.role) updateData.role = editForm.role;
      if (editForm.airport !== undefined) updateData.airport = editForm.airport;
      if (editForm.enabled !== undefined) updateData.enabled = editForm.enabled;

      await userService.updateUser(editingUser.id, updateData);
      setEditingUser(null);
      setEditForm({
        email: '',
        username: '',
        password: '',
        name: '',
        surname: '',
        patronymic: '',
        role: 'USER',
        position: 'DRIVER',
        airport: '',
        enabled: true
      });
      fetchUsers();
      alert('Пользователь успешно обновлен!');
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Ошибка при обновлении пользователя');
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!window.confirm(`Вы уверены, что хотите удалить пользователя "${user.name} ${user.surname}"?`)) {
      return;
    }

    try {
      await userService.deleteUser(user.id);
      fetchUsers();
      alert('Пользователь успешно удален!');
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Ошибка при удалении пользователя');
    }
  };

  const handleToggleUserStatus = async (user: User) => {
    try {
      await userService.toggleUserStatus(user.id, !user.enabled);
      fetchUsers();
      alert(`Пользователь ${user.enabled ? 'деактивирован' : 'активирован'}!`);
    } catch (err) {
      console.error('Error toggling user status:', err);
      alert('Ошибка при изменении статуса пользователя');
    }
  };

  const handleResetPassword = async (user: User) => {
    if (!window.confirm(`Сбросить пароль для пользователя "${user.name} ${user.surname}"?`)) {
      return;
    }

    try {
      const result = await userService.resetPassword(user.id);
      alert(`Временный пароль: ${result.temporaryPassword}`);
    } catch (err) {
      console.error('Error resetting password:', err);
      alert('Ошибка при сбросе пароля');
    }
  };

  const handleBulkAction = async (action: 'activate' | 'deactivate' | 'delete' | 'resetPassword') => {
    if (selectedUsers.length === 0) {
      alert('Выберите пользователей для выполнения действия');
      return;
    }

   

    try {
      for (const userId of selectedUsers) {
        switch (action) {
          case 'activate':
            await userService.toggleUserStatus(userId, true);
            break;
          case 'deactivate':
            await userService.toggleUserStatus(userId, false);
            break;
          case 'delete':
            await userService.deleteUser(userId);
            break;
          case 'resetPassword':
            const result = await userService.resetPassword(userId);
            alert(`Временный пароль для пользователя ${userId}: ${result.temporaryPassword}`);
            break;
        }
      }
      setSelectedUsers([]);
      fetchUsers();
      fetchUserStats();
      alert(`Действие выполнено для ${selectedUsers.length} пользователей`);
    } catch (err) {
      console.error('Error performing bulk action:', err);
      alert('Ошибка при выполнении действия');
    }
  };

  const handleSelectUser = (userId: number) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.id));
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortDir('ASC');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.surname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesActive = !showActiveOnly || user.enabled;
    const matchesRole = !selectedRole || user.role === selectedRole;
    const matchesAirport = !selectedAirport || user.airport === selectedAirport;
    return matchesSearch && matchesActive && matchesRole && matchesAirport;
  });

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setEditForm({
      email: user.email,
      username: user.username,
      password: '', // Clear password when opening edit modal
      name: user.name,
      surname: user.surname,
      patronymic: user.patronymic || '',
      role: user.role,
      position: user.position || '',
      airport: user.airport || '',
      enabled: user.enabled
    });
  };

  const closeEditModal = () => {
    setEditingUser(null);
    setEditForm({
      email: '',
      username: '',
      password: '',
      name: '',
      surname: '',
      patronymic: '',
      role: 'USER',
      position: 'DRIVER',
      airport: '',
      enabled: true
    });
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return '#ef4444';
      case 'MODERATOR':
        return '#f59e0b';
      case 'USER':
        return '#3b82f6';
      default:
        return '#6b7280';
    }
  };

  const getRoleText = (role: string) => {
    const roleMap: Record<string, string> = {
      'USER': 'Пользователь',
      'MODERATOR': 'Модератор',
      'ADMIN': 'Администратор'
    };
    return roleMap[role] || role;
  };

  const getAirportText = (airport: string | undefined) => {
    if (!airport) return '-';
    const airportMap: Record<string, string> = {
      'SVO': 'Шереметьево',
      'DME': 'Домодедово',
      'VKO': 'Внуково',
      'ASTANA': 'Астана',
      'ALMATY': 'Алматы'
    };
    return airportMap[airport] || airport;
  };

  const getPositionText = (position: string) => {
    const positionMap: Record<string, string> = {
      'DRIVER': 'Водитель',
      'MANAGER': 'Менеджер',
      'OPERATOR': 'Оператор'
    };
    return positionMap[position] || position;
  };

  if (loading) {
    return (
      <div className="user-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка пользователей...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-header">
        <div className="header-left">
          <h2>Управление пользователями</h2>
          {userStats && (
            <div className="quick-stats">
              <span className="stat-item">
                <strong>{totalElements}</strong> всего
              </span>
              <span className="stat-item">
                <strong>{userStats.activeUsers}</strong> активных
              </span>
              <span className="stat-item">
                <strong>{userStats.inactiveUsers}</strong> неактивных
              </span>
            </div>
          )}
        </div>
        <div className="user-actions">
          <button 
            onClick={() => setIsCreateModalOpen(true)} 
            className="btn create-user-btn"
          >
            Создать пользователя
          </button>
          <div className="bulk-actions">
            <button onClick={() => handleBulkAction('resetPassword')} className="btn bulk-reset-btn">
              Сбросить пароли
            </button>
            <button onClick={() => handleBulkAction('delete')} className="btn bulk-delete-btn">
              Удалить выбранных
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchUsers} className="retry-button">
            Попробовать снова
          </button>
        </div>
      )}

      <div className="user-filters">
        <div className="search-section">
          <input
            type="text"
            placeholder="Поиск по имени, фамилии, email или username..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-section">
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="role-filter"
          >
            <option value="">Все роли</option>
            {roles.map(role => (
              <option key={role} value={role}>{getRoleText(role)}</option>
            ))}
          </select>
          <select
            value={selectedAirport}
            onChange={(e) => setSelectedAirport(e.target.value)}
            className="airport-filter"
          >
            <option value="">Все аэропорты</option>
            {airports.map(airport => (
              <option key={airport} value={airport}>{getAirportText(airport)}</option>
            ))}
          </select>
          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.target.checked)}
            />
            Только активные
          </label>
        </div>
        <div className="view-controls">
          <button onClick={() => setViewMode(viewMode === 'cards' ? 'table' : 'cards')} className="view-toggle-btn">
            {viewMode === 'cards' ? 'Список' : 'Карточки'}
          </button>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="page-size-select"
          >
            <option value={5}>5 на странице</option>
            <option value={10}>10 на странице</option>
            <option value={20}>20 на странице</option>
            <option value={50}>50 на странице</option>
          </select>
        </div>
      </div>

      <div className="users-list">
        {viewMode === 'cards' ? (
          filteredUsers.map((user) => (
            <div key={user.id} className={`user-card ${!user.enabled ? 'inactive' : ''}`}>
              <div className="user-info">
                <div className="user-header-info">
                  <div className="user-avatar">
                    {user.name.charAt(0)}{user.surname.charAt(0)}
                  </div>
                  <div className="user-name-info">
                    <h3 className="user-name">{user.name} {user.surname}</h3>
                    <p className="user-email">{user.email}</p>
                    <p className="user-username">@{user.username}</p>
                  </div>
                  <div className="user-badges">
                    <span 
                      className="role-badge"
                      style={{ backgroundColor: getRoleColor(user.role) }}
                    >
                      {getRoleText(user.role)}
                    </span>
                    <span className={`status-badge ${user.enabled ? 'active' : 'inactive'}`}>
                      {user.enabled ? 'Активен' : 'Неактивен'}
                    </span>
                  </div>
                </div>
                <div className="user-additional-info">
                  <p><strong>Должность:</strong> {getPositionText(user.position)}</p>
                  {user.airport && <p><strong>Аэропорт:</strong> {getAirportText(user.airport)}</p>}
                  {user.patronymic && <p><strong>Отчество:</strong> {user.patronymic}</p>}
                </div>
                <div className="user-meta">
                  <span>Создан: {new Date(user.createdAt).toLocaleDateString('ru-RU')}</span>
                  <span>Обновлен: {new Date(user.updatedAt).toLocaleDateString('ru-RU')}</span>
                </div>
              </div>
              <div className="user-actions">
                <button 
                  onClick={() => openEditModal(user)} 
                  className="btn edit-btn"
                >
                  Редактировать
                </button>
                
                <button 
                  onClick={() => handleResetPassword(user)} 
                  className="btn reset-password-btn"
                >
                  Сбросить пароль
                </button>
                <button 
                  onClick={() => handleDeleteUser(user)} 
                  className="btn delete-btn"
                >
                  Удалить
                </button>
              </div>
            </div>
          ))
        ) : (
          <table className="user-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th onClick={() => handleSort('id')} className="sortable">
                  ID {sortBy === 'id' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('name')} className="sortable">
                  Имя {sortBy === 'name' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('surname')} className="sortable">
                  Фамилия {sortBy === 'surname' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('email')} className="sortable">
                  Email {sortBy === 'email' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('username')} className="sortable">
                  Username {sortBy === 'username' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('role')} className="sortable">
                  Роль {sortBy === 'role' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th>Статус</th>
                <th onClick={() => handleSort('airport')} className="sortable">
                  Аэропорт {sortBy === 'airport' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('position')} className="sortable">
                  Должность {sortBy === 'position' && (sortDir === 'ASC' ? '↑' : '↓')}
                </th>
                <th>Отчество</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className={`user-row ${!user.enabled ? 'inactive' : ''}`}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleSelectUser(user.id)}
                    />
                  </td>
                  <td>{user.id}</td>
                  <td>{user.name}</td>
                  <td>{user.surname}</td>
                  <td>{user.email}</td>
                  <td>@{user.username}</td>
                  <td>
                    <span 
                      className="role-badge"
                      style={{ backgroundColor: getRoleColor(user.role) }}
                    >
                      {getRoleText(user.role)}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.enabled ? 'active' : 'inactive'}`}>
                      {user.enabled ? 'Активен' : 'Неактивен'}
                    </span>
                  </td>
                  <td>{getAirportText(user.airport)}</td>
                  <td>{getPositionText(user.position)}</td>
                  <td>{user.patronymic}</td>
                  <td>
                    <button 
                      onClick={() => openEditModal(user)} 
                      className="btn edit-btn"
                    >
                      Редактировать
                    </button>
                    <button 
                      onClick={() => handleToggleUserStatus(user)} 
                      className={`btn toggle-status-btn ${user.enabled ? 'deactivate' : 'activate'}`}
                    >
                      {user.enabled ? 'Деактивировать' : 'Активировать'}
                    </button>
                    <button 
                      onClick={() => handleResetPassword(user)} 
                      className="btn reset-password-btn"
                    >
                      Сбросить пароль
                    </button>
                    <button 
                      onClick={() => handleDeleteUser(user)} 
                      className="btn delete-btn"
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {filteredUsers.length === 0 && !loading && (
        <div className="empty-state">
          <p>Пользователи не найдены</p>
        </div>
      )}

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <button onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0}>Назад</button>
          <span>Страница {currentPage + 1} из {totalPages}</span>
          <button onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage === totalPages - 1}>Вперед</button>
        </div>
      )}

      {/* Статистика */}
      {userStats && (
        <div className="user-stats">
          <h3>Статистика пользователей</h3>
          <p><strong>Всего пользователей:</strong> {totalElements}</p>
          <p><strong>Активных пользователей:</strong> {userStats.activeUsers || 0}</p>
          <p><strong>Неактивных пользователей:</strong> {userStats.inactiveUsers || 0}</p>
          {userStats.usersByRole && (
            <div className="role-stats">
              <h4>Пользователи по ролям:</h4>
              {Object.entries(userStats.usersByRole).map(([role, count]) => (
                <p key={role}><strong>{getRoleText(role)}:</strong> {String(count)}</p>
              ))}
            </div>
          )}
          {userStats.usersByAirport && (
            <div className="airport-stats">
              <h4>Пользователи по аэропортам:</h4>
              {Object.entries(userStats.usersByAirport).map(([airport, count]) => (
                <p key={airport}><strong>{getAirportText(airport)}:</strong> {String(count)}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Модальное окно создания */}
      {isCreateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Создать нового пользователя</h3>
              <button onClick={() => setIsCreateModalOpen(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    placeholder="user@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Username *</label>
                  <input
                    type="text"
                    value={createForm.username}
                    onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                    placeholder="username"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Пароль *</label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  placeholder="Введите пароль"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Имя *</label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    placeholder="Имя"
                  />
                </div>
                <div className="form-group">
                  <label>Фамилия *</label>
                  <input
                    type="text"
                    value={createForm.surname}
                    onChange={(e) => setCreateForm({ ...createForm, surname: e.target.value })}
                    placeholder="Фамилия"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Отчество</label>
                <input
                  type="text"
                  value={createForm.patronymic}
                  onChange={(e) => setCreateForm({ ...createForm, patronymic: e.target.value })}
                  placeholder="Отчество"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Роль *</label>
                  <select
                    value={createForm.role}
                    onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                  >
                    {roles.map(role => (
                      <option key={role} value={role}>{getRoleText(role)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Аэропорт</label>
                  <select
                    value={createForm.airport}
                    onChange={(e) => setCreateForm({ ...createForm, airport: e.target.value })}
                  >
                    <option value="">Выберите аэропорт</option>
                    {airports.map(airport => (
                      <option key={airport} value={airport}>{getAirportText(airport)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Должность</label>
                <select
                  value={createForm.position}
                  onChange={(e) => setCreateForm({ ...createForm, position: e.target.value })}
                >
                  {positions.map(position => (
                    <option key={position} value={position}>{getPositionText(position)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={createForm.enabled}
                    onChange={(e) => setCreateForm({ ...createForm, enabled: e.target.checked })}
                  />
                  Активен
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setIsCreateModalOpen(false)} className="cancel-btn">
                Отмена
              </button>
              <button onClick={handleCreateUser} className="save-btn">
                Создать
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно редактирования */}
      {editingUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Редактировать пользователя</h3>
              <button onClick={closeEditModal} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    placeholder="user@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Username</label>
                  <input
                    type="text"
                    value={editForm.username}
                    onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                    placeholder="username"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Новый пароль (оставьте пустым, чтобы не изменять)</label>
                <input
                  type="password"
                  value={editForm.password || ''}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  placeholder="Введите новый пароль"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Имя</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    placeholder="Имя"
                  />
                </div>
                <div className="form-group">
                  <label>Фамилия</label>
                  <input
                    type="text"
                    value={editForm.surname}
                    onChange={(e) => setEditForm({ ...editForm, surname: e.target.value })}
                    placeholder="Фамилия"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Отчество</label>
                <input
                  type="text"
                  value={editForm.patronymic}
                  onChange={(e) => setEditForm({ ...editForm, patronymic: e.target.value })}
                  placeholder="Отчество"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Роль</label>
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  >
                    {roles.map(role => (
                      <option key={role} value={role}>{getRoleText(role)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Аэропорт</label>
                  <select
                    value={editForm.airport}
                    onChange={(e) => setEditForm({ ...editForm, airport: e.target.value })}
                  >
                    <option value="">Выберите аэропорт</option>
                    {airports.map(airport => (
                      <option key={airport} value={airport}>{getAirportText(airport)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Должность</label>
                <select
                  value={editForm.position}
                  onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
                >
                  {positions.map(position => (
                    <option key={position} value={position}>{getPositionText(position)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editForm.enabled}
                    onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })}
                  />
                  Активен
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={closeEditModal} className="cancel-btn">
                Отмена
              </button>
              <button onClick={handleUpdateUser} className="save-btn">
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 